import { Accordion, AccordionDetails, AccordionSummary, Box, IconButton } from "@mui/material";
import Icon from "@mui/material/Icon";
import DeleteIcon from "@mui/icons-material/Delete";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { ProposalProduct, ProposalStage } from "generated/graphql";
import StageProduct from "./StageProduct";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { memo } from "react";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import ToggleProposalProductOptionality from "./ToggleProposalProductOptionality";
import InlineEditableField from "components/InlineEditableField/InlineEditableField";
import { gridTemplateColumns } from "./ProductTableHeader";
import { validateCurrency, validatePercentage } from "./validators";

export interface ProductRowProps {
  product: ProposalProduct;
  isExpanded: boolean;
  inViewMode: boolean;
  sortMode: boolean;
  onToggle: (productId: string, isExpanded: boolean) => void;
  onDelete: (productId: string, e: React.MouseEvent) => void;
  onUpdateName: (productId: string, value: string) => Promise<void>;
  onUpdateProfit: (productId: string, value: string) => Promise<void>;
  onUpdateMargin: (productId: string, value: string) => Promise<void>;
  onUpdateDiscount: (productId: string, value: string) => Promise<void>;
  onUpdateTotal: (productId: string, value: string) => Promise<void>;
  onUpdateUnit: (productId: string, value: string) => Promise<void>;
  stage: ProposalStage;
  handleDelete: (id: string) => Promise<{ success: boolean; data: any }>;
}

const parseCurrencyToCents = (value: string) => {
  if (value.trim() === "") return "";
  const num = parseFloat(value.replace(/[^0-9.-]/g, ""));
  return isNaN(num) ? "" : String(num);
};

const ProductRow = memo(function ProductRow({
  product,
  isExpanded,
  inViewMode,
  sortMode,
  onToggle,
  onDelete,
  onUpdateName,
  onUpdateProfit,
  onUpdateMargin,
  onUpdateDiscount,
  onUpdateTotal,
  onUpdateUnit,
  stage,
  handleDelete,
}: ProductRowProps) {
  const productName = product.material?.externalProductName || product.material?.name || "";
  const profit = product.profitAfterDiscount || product.profitOverride || product.profit || "0";
  const profitMargin = product.profitPercentageOverride ?? product.profitPercentage ?? 0;
  const discount = product.discount || "0";

  const effectiveTotal = Number(
    product.overallTotalOverride ?? product.overallTotalAfterDiscount ?? product.overallTotal ?? 0
  );
  const costTotal = +(product.costTotal ?? 0);
  const isClampedToCost = effectiveTotal < costTotal;
  const overallTotal = isClampedToCost ? costTotal : effectiveTotal;
  const totalPrice = String(overallTotal);
  const calculatedUnitPrice = product.jobSize ? overallTotal / product.jobSize : 0;
  const isHeaderUnitPriceProduct =
    !!product.material?.unitPriceProduct || !!product.material?.subcontracted;
  const effectiveUnitPriceOverride =
    isHeaderUnitPriceProduct
      ? product.unitPriceOverride ?? null
      : product.material?.unitPriceOverride ?? product.unitPriceOverride ?? null;
  const unitPriceIsBelowCost =
    effectiveUnitPriceOverride != null &&
    product.jobSize > 0 &&
    Number(effectiveUnitPriceOverride) * product.jobSize < costTotal;
  const unitPrice = isClampedToCost || unitPriceIsBelowCost || isHeaderUnitPriceProduct
    ? calculatedUnitPrice
    : Number(effectiveUnitPriceOverride ?? calculatedUnitPrice);

  return (
    <MDBox position="relative">
      <div
        id={`panel-product-${product.id}`}
        style={{
          position: "absolute",
          top: "-230px",
          pointerEvents: "none",
          visibility: "hidden",
        }}
      />
      <Accordion
        expanded={isExpanded}
        elevation={0}
        onChange={(_, expanded) => onToggle(product.id, expanded)}
        TransitionProps={{ unmountOnExit: true, timeout: 150 }}
        sx={{
          "&:before": { display: "none" },
          boxShadow: "none",
          borderBottom: "1px solid rgba(100, 100, 150, 0.2)",
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`panel-product-${product.id}-content`}
          sx={{
            backgroundColor: isExpanded ? "rgba(100, 100, 150, 0.25)" : "rgba(100, 100, 150, 0.08)",
            minHeight: "48px",
            "&.Mui-expanded": { minHeight: "48px" },
            "& .MuiAccordionSummary-content": { margin: "8px 0", overflow: "visible" },
            "&:hover": { backgroundColor: "rgba(100, 100, 150, 0.15)" },
            px: 2,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns,
              gap: 2,
              width: "100%",
              alignItems: "center",
            }}
          >
            <Box display="flex" alignItems="center">
              {sortMode && (
                <Icon fontSize="small" sx={{ mr: 0.5 }}>
                  sort
                </Icon>
              )}
              <ToggleProposalProductOptionality id={product.id} isOptional={product.isOptional} />
            </Box>

            <Box display="flex" alignItems="flex-start" flexDirection="column" sx={{ minWidth: 0 }}>
              {!inViewMode ? (
                <InlineEditableField
                  value={productName}
                  onSave={(value) => onUpdateName(product.id, value)}
                  fontSize="0.95rem"
                  minWidth="100px"
                />
              ) : (
                <MDTypography variant="body" sx={{ fontSize: "0.95rem", wordBreak: "break-word" }}>
                  {productName}
                </MDTypography>
              )}
              {product.material?.subcontracted && (
                <MDTypography variant="caption" sx={{ color: "text.secondary" }}>
                  (Subcontracted)
                </MDTypography>
              )}
            </Box>

            <MDTypography variant="body" sx={{ fontSize: "0.95rem" }}>
              {formatCentsToUSD(parseFloat(product.costTotal))}
            </MDTypography>

            {!inViewMode ? (
              <InlineEditableField
                value={(parseFloat(profit) / 100).toFixed(2)}
                onSave={(value) => onUpdateProfit(product.id, value)}
                formatDisplay={(val) => formatCentsToUSD(parseFloat(val) * 100)}
                parseInput={parseCurrencyToCents}
                type="currency"
                fontSize="0.95rem"
                minWidth="70px"
                validate={validateCurrency}
              />
            ) : (
              <MDTypography variant="body" sx={{ fontSize: "0.95rem" }}>
                {formatCentsToUSD(parseFloat(profit))}
              </MDTypography>
            )}

            {!inViewMode ? (
              <InlineEditableField
                value={String(profitMargin)}
                onSave={(value) => onUpdateMargin(product.id, value)}
                formatDisplay={(val) => `${val}%`}
                type="percentage"
                fontSize="0.95rem"
                minWidth="50px"
                validate={validatePercentage}
              />
            ) : (
              <MDTypography variant="body" sx={{ fontSize: "0.95rem" }}>
                {profitMargin}%
              </MDTypography>
            )}

            {!inViewMode ? (
              <InlineEditableField
                value={(parseFloat(discount) / 100).toFixed(2)}
                onSave={(value) => onUpdateDiscount(product.id, value)}
                formatDisplay={(val) => formatCentsToUSD(parseFloat(val) * 100)}
                parseInput={parseCurrencyToCents}
                type="currency"
                fontSize="0.95rem"
                minWidth="70px"
                validate={validateCurrency}
              />
            ) : (
              <MDTypography variant="body" sx={{ fontSize: "0.95rem" }}>
                {formatCentsToUSD(parseFloat(discount))}
              </MDTypography>
            )}

            {!inViewMode ? (
              <InlineEditableField
                value={(parseFloat(totalPrice) / 100).toFixed(2)}
                onSave={(value) => onUpdateTotal(product.id, value)}
                formatDisplay={(val) => formatCentsToUSD(parseFloat(val) * 100)}
                parseInput={parseCurrencyToCents}
                type="currency"
                fontSize="0.95rem"
                minWidth="80px"
                validate={validateCurrency}
              />
            ) : (
              <MDTypography variant="body" sx={{ fontSize: "0.95rem" }}>
                {formatCentsToUSD(parseFloat(totalPrice))}
              </MDTypography>
            )}

            {!inViewMode ? (
              <InlineEditableField
                value={(unitPrice / 100).toFixed(2)}
                onSave={(value) => onUpdateUnit(product.id, value)}
                formatDisplay={(val) => formatCentsToUSD(parseFloat(val) * 100)}
                parseInput={parseCurrencyToCents}
                type="currency"
                fontSize="0.95rem"
                minWidth="70px"
                validate={validateCurrency}
              />
            ) : (
              <MDTypography variant="body" sx={{ fontSize: "0.95rem" }}>
                {formatCentsToUSD(unitPrice)}
              </MDTypography>
            )}

            {!inViewMode ? (
              <IconButton
                size="small"
                onClick={(e) => onDelete(product.id, e)}
                sx={{
                  color: "error.main",
                  padding: "4px",
                  "&:hover": { backgroundColor: "error.light", color: "error.dark" },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            ) : (
              <Box />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2 }}>
          <StageProduct
            stage={stage}
            expandedProduct={isExpanded ? product.id : false}
            product={product}
            transferTrucks={[]}
            mapData={stage.mapData}
            handleDelete={handleDelete}
          />
        </AccordionDetails>
      </Accordion>
    </MDBox>
  );
});

export default ProductRow;
